<template>
    <detail-header
        pretitle="Documentation Links"
        :title="docLink ? docLink.title : '&nbsp;'"
        :editable="true"
        :editMode="editMode"
        @editClick="onEditClick"
        @deleteClick="onDeleteClick"
        @saveClick="onSaveClick"
        @cancelClick="onCancelClick"
        @closeClick="$router.push('/admin/documentations')"
    />

    <div class="container-fluid">
        <div v-if="error" class="alert alert-danger" role="alert">
            {{ error }}
        </div>
        <div v-if="successMessage" class="alert alert-success" role="alert">
            {{ successMessage }}
        </div>
        <div v-if="docLink">
            <div class="row">
                <div class="col">
                    <div class="card">
                        <div class="card-body">
                            <form-fields
                                :fields="fields"
                                v-model="docLink"
                                :editMode="editMode"
                                :inlineField="true"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="text-center">
            <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>
    </div>
</template>

<script>
import DetailHeader from "../../components/DetailHeader.vue";
import Fields, { getRouteOptions } from "../../utils/field-constants/docLinkFields";
import FormFields from "../../components/FormFields.vue";
import { mapActions } from "vuex";
import router from '../../router'

export default {
    name: "DocLink",
    components: { DetailHeader, FormFields },
    data() {
        return {
            editMode: false,
            docLink: null,
            snapshot: null,
            error: "",
            successMessage: "",
            fields: [],
            routeOptions: []
        };
    },
    watch: {
        'docLink.route_name': {
            handler(newroute_name) {
                if (newroute_name && this.editMode) {
                    // Find the selected route and populate the path
                    const selectedRoute = this.routeOptions.find(option => option.value === newroute_name)
                    if (selectedRoute) {
                        this.docLink.route_path = selectedRoute.path
                    }
                }
            },
            immediate: true
        }
    },
    beforeMount() {
        this.initializeFields();
        this.fetchDocLink();
    },
    methods: {
        ...mapActions({
            readDocLink: "admin/readDocLink",
            updateDocLink: "admin/updateDocLink",
            deleteDocLink: "admin/deleteDocLink",
            showModal: "showModal",
        }),
        initializeFields() {
            // Get route options
            this.routeOptions = getRouteOptions(router)
            
            // Get existing doc links for duplicate validation (excluding current item)
            const existingDocLinks = this.$store.state.admin.docLinks || []
            
            // Update the fields with route options and validation
            this.fields = Fields.map(field => {
                if (field.name === 'route_name' && field.getOptions) {
                    return {
                        ...field,
                        options: this.routeOptions,
                        validation: [
                            ...field.validation,
                            {
                                type: 'duplicate',
                                list: existingDocLinks,
                                fieldName: 'route_name',
                                referenceName: this.docLink?.route_name,
                                errMsg: 'A documentation link with this route name already exists'
                            }
                        ]
                    }
                } else if (field.name === 'title') {
                    return {
                        ...field,
                        validation: [
                            ...field.validation,
                            {
                                type: 'duplicate',
                                list: existingDocLinks,
                                fieldName: 'title',
                                referenceName: this.docLink?.title,
                                errMsg: 'A documentation link with this title already exists'
                            }
                        ]
                    }
                }
                field.visible = true;
                return field
            })
        },
        setEditMode(mode) {
            this.editMode = mode;
            this.successMessage = null;
            this.error = null;
        },
        async fetchDocLink() {
            let response = {};
            if (!this.$route.params.data) {
                try {
                    response = await this.readDocLink(this.$route.params.id);
                } catch (error) {
                    if (error.response?.data?.error)
                        this.error = error.response.data.error;
                    else this.error = error.message;
                    return;
                }
            } else {
                response["data"] = JSON.parse(this.$route.params.data);
            }
            this.docLink = await response.data;
            this.snapshot = { ...this.docLink };
            // Reinitialize fields after docLink is loaded for proper validation
            this.initializeFields();
        },
        onCancelClick() {
            this.setEditMode(false);
            this.docLink = { ...this.snapshot };
            this.error = null;
        },
        onEditClick() {
            this.setEditMode(true);
        },
        onDeleteClick() {
            const self = this;
            this.showModal({
                component: "confirm",
                data: {
                    title: "Delete Documentation Link",
                    message: `Are you sure you want to delete ${this.docLink.title}?`,
                    async confirm() {
                        try {
                            const response = await self.deleteDocLink(self.docLink.id);
                            self.$router.push("/admin/documentations");
                            return response;
                        } catch (error) {
                            if (error.response?.data?.error)
                                self.error = error.response.data.error;
                            else self.error = error.message;
                            return;
                        }
                    },
                },
            });
        },
        async onSaveClick() {
            var self = this;
            this.error = "";
            
            try {
                const response = await this.updateDocLink(this.docLink);
                this.setEditMode(false);
                this.snapshot = { ...this.docLink };
                this.successMessage = "Documentation link updated successfully.";
            } catch (error) {
                if (error.response?.data?.error)
                    self.error = error.response.data.error;
                else self.error = error.message;
            }
        },
    },
};
</script>

<style>
</style> 